<template>
  <v-form ref="form" v-model="valid">
    <v-row>
      <v-col class="py-0">
        <div>
          <anchor-text-field
            id="role-level-form-name-input"
            required
            label="Role level Name"
            :disabled="hasNotCreatePermission"
            v-model="model.name"
            :rules="rules.name"
          />
        </div>
        <div>
          <v-autocomplete
            id="role-level-form-role-input"
            dense
            required
            outlined
            label="Role"
            :items="roles"
            item-text="title"
            item-value="_id"
            v-model="model.role"
            :rules="rules.role"
            :disabled="hasNotCreatePermission"
          />
        </div>
        <div>
          <v-autocomplete
            id="role-level-form-parent-level-input"
            dense
            outlined
            clearable
            label="Parent Level"
            :items="roleLevels"
            item-text="name"
            item-value="_id"
            v-model="model.parent"
            :disabled="hasNotCreatePermission"
          />
        </div>
        <div>
          <label class="body-1">
            Specialized Groups <small>(not in parent role)</small>
          </label>
          <anchor-list-select
            id="role-level-form-role-select"
            class="mt-2"
            height="400"
            v-model="model.groups"
            :options="selectableGroups"
            :loading="loading"
            :error="error"
          />
        </div>
      </v-col>
    </v-row>
    <div class="d-flex">
      <v-spacer />
      <anchor-form-buttons
        submit-text="save"
        :valid="valid"
        @submit="submit"
        @cancel="cancel"
      />
    </div>
  </v-form>
</template>

<script>
/* eslint no-underscore-dangle: ["error", { "allow": ["_id"] }] */
import LEVEL_MODEL from './resources/model';
import LEVEL_RULES from './resources/rules';

export default {
  name: 'AdminRoleLevelsForm',
  data: () => ({
    valid: false,
    model: {},
    rules: LEVEL_RULES,
    groups: [],
    roles: [],
    roleLevels: [],
    loading: true,
    error: null,
    originalGroups: [],
  }),
  computed: {
    user: vm => vm.$store.getters.user,
    hasNotCreatePermission: vm => !vm.user.permissions.includes(vm.$permissions.ADD_ROLE_LEVELS),
    allowedDomains() {
      return (this.$store.getters.role?.domains || []).map(domain => domain._id);
    },
    selectableGroups() {
      const groups = this.groups.filter(group => this.allowedDomains.includes(group.domain));
      const groupsNames = this.$utils.sortIgnoringCase(Array.from(new Set(groups.map(item => item.name))));
      if (this.model.role) {
        return groupsNames.filter(g => !this.roles.find(r => r._id === this.model.role)?.groups.includes(g));
      }
      return groupsNames;
    },
    selectedGroups() {
      if (this.originalGroups.length) {
        const groups = this.groups.filter(group => this.originalGroups.includes(group.name));
        const otherDomainsGroups = groups.filter(group => !this.allowedDomains.includes(group.domain));
        return Array.from(new Set(this.model.groups.concat(otherDomainsGroups.map(g => g.name))));
      }
      return this.model.groups;
    },
  },
  methods: {
    setModel(level) {
      this.$refs.form.reset();
      if (level) {
        const mdl = {};
        Object.keys(LEVEL_MODEL).forEach(key => {
          mdl[key] = level[key];
        });
        mdl.id = level._id;
        mdl.role = mdl.role._id;
        if (level.parent != null) {
          mdl.parent = level.parent._id;
        }
        this.originalGroups = JSON.parse(JSON.stringify(mdl.groups));
        this.$set(this, 'model', mdl);
      } else {
        this.model = this.$utils.deepCopy(LEVEL_MODEL);
      }
    },
    submit() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.loading = true;
      const method = this.model.id ? 'put' : 'post';
      const url = this.model.id ? `/v1/role_levels/${this.model.id}` : '/v1/role_levels';
      const data = this.$utils.deepCopy(this.model);
      data.groups = this.selectedGroups;
      delete data.id;

      this.$requests[method](url, data)
        .then(() => {
          this.$toast.success('Role level successfully saved!', { timeout: 3000 });
          this.$emit('success');
        })
        .catch(err => {
          this.$toast.error(`Unable to save role level. ${err.response.data?.message || ''}`, { timeout: 5000 });
        })
        .then(() => {
          this.loading = false;
        });
    },
    getGroups() {
      this.loading = true;
      this.$requests
        .get('/v1/groups?grouped=false')
        .then(response => {
          this.groups = response.data;
          this.$set(
            this.model,
            'groups',
            this.model.groups.filter(g => this.selectableGroups.includes(g)),
          );
        })
        .catch(() => {
          this.$toast.error('Could not resolve list of groups...', { timeout: 3000 });
          this.error = true;
        })
        .then(() => {
          this.loading = false;
        });
    },
    getRoles() {
      this.loading = true;
      this.$requests
        .get('/v1/roles')
        .then(response => {
          function sortRoles(roleA, roleB) {
            return roleA.title < roleB.title ? -1 : 1;
          }
          const roles = response.data.sort(sortRoles);
          this.roles = roles;
        })
        .catch(() => {
          this.$toast.error('Could not resolve list of roles...', { timeout: 3000 });
          this.error = true;
        })
        .then(() => {
          this.loading = false;
        });
    },
    getRoleLevels() {
      this.loading = true;
      this.$requests
        .get('/v1/role_levels')
        .then(response => {
          function sortRoleLevels(roleA, roleB) {
            return roleA.name < roleB.name ? -1 : 1;
          }
          const roleLevels = response.data.sort(sortRoleLevels);
          this.roleLevels = roleLevels;
        })
        .catch(() => {
          this.$toast.error('Could not resolve list of role levels...', { timeout: 3000 });
          this.error = true;
        })
        .then(() => {
          this.loading = false;
        });
    },
    reset() {
      this.$utils.deepCopy(LEVEL_MODEL);
      this.getGroups();
      this.getRoles();
      this.getRoleLevels();
    },
    cancel() {
      this.reset();
      this.$emit('cancel');
    },
  },
  watch: {
    'model.role': function fn(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$set(this.model, 'role', newValue);
        if (!this.model.id || (oldValue && this.model.id)) {
          this.$set(this.model, 'groups', []);
          this.originalGroups = [];
        }
      }
    },
  },
  mounted() {
    this.reset();
  },
};
</script>
